import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { ArrowRight, GraduationCap, MapPin, ChevronLeft, ChevronRight, SlidersHorizontal, ChevronDown, ChevronUp, X, UserCheck, Clock, BookOpen, Languages, Tag, Award, Info } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useBranding } from "@/context/BrandingContext";
import ProgramBasketWidget from "@/components/ProgramBasketWidget";
import { formatDuration } from "@/utils/helpers";

// Interface for a program row from price_list_entry + program join
interface UIProgram {
    id: number;
    program_code: string;
    name: string;
    degree_type: string;
    faculty: string;
    language: string;
    mode_of_study: string;
    duration: string;
    thesis_requirement: string;
    tuition_fee: number | null;
    currency_code: string;
    available_application_types: string[];
}

// Interface for UI term structure
interface UITerm {
    id: string;
    name: string;
    salesforce_id: string;
}

// Interface for a degree level from Hub DB
interface UIDegree {
    id: string;
    degree: string;
    max_ple_for_same_institution?: number | null;
}


export default function Home() {
    const navigate = useNavigate();
    const [selectedTerm, setSelectedTerm] = useState("");
    const [activeLevel, setActiveLevel] = useState("Undergraduate");
    const [selectedProgramIds, setSelectedProgramIds] = useState<number[]>([]);
    const [programs, setPrograms] = useState<UIProgram[]>([]);
    const [programsLoading, setProgramsLoading] = useState(true);
    const [programsError, setProgramsError] = useState<string | null>(null);
    const [terms, setTerms] = useState<UITerm[]>([]);
    const [termsLoading, setTermsLoading] = useState(true);
    const [degrees, setDegrees] = useState<UIDegree[]>([]);
    const [degreesLoading, setDegreesLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [nameFilter, setNameFilter] = useState("");
    const [durationFilter, setDurationFilter] = useState("");
    const [languageFilter, setLanguageFilter] = useState("");
    const [facultyFilter, setFacultyFilter] = useState("");
    const [thesisFilter, setThesisFilter] = useState("");
    const [tuitionFeeMin, setTuitionFeeMin] = useState("");
    const [tuitionFeeMax, setTuitionFeeMax] = useState("");
    const [applicationTypeFilter, setApplicationTypeFilter] = useState("");
    const { branding, loading } = useBranding();
    const programCacheRef = useRef<Record<string, UIProgram[]>>({});

    // Fetch intake terms from Hub DB
    useEffect(() => {
        const fetchTerms = async () => {
            try {
                setTermsLoading(true);
                const response = await fetch('/api/integrations/hub/terms/');
                if (!response.ok) throw new Error(`Terms fetch failed: ${response.status}`);
                const data = await response.json();
                if (data.status === 'success' && Array.isArray(data.terms)) {
                    const uiTerms: UITerm[] = data.terms.map((t: { id: string; name: string; salesforce_id: string }) => ({
                        id: t.id,
                        name: t.name,
                        salesforce_id: t.salesforce_id,
                    }));
                    setTerms(uiTerms);
                    if (uiTerms.length > 0) setSelectedTerm(uiTerms[0].id);
                }
            } catch (error) {
                console.error('Error fetching terms:', error);
            } finally {
                setTermsLoading(false);
            }
        };
        fetchTerms();
    }, []);

    // Fetch degree levels from Hub DB filtered by selected term
    useEffect(() => {
        if (!selectedTerm) return;
        const fetchDegrees = async () => {
            try {
                setDegreesLoading(true);
                const params = `?term_id=${encodeURIComponent(selectedTerm)}`;
                const response = await fetch(`/api/integrations/hub/degrees/${params}`);
                if (!response.ok) {
                    throw new Error(`Degrees fetch failed: ${response.status}`);
                }
                const data = await response.json();
                if (data.status === 'success' && Array.isArray(data.degrees) && data.degrees.length > 0) {
                    const mappedDegrees: UIDegree[] = data.degrees.map((degree: { id: string; degree: string; max_ple_for_same_institution?: number | null }) => ({
                        id: degree.id,
                        degree: degree.degree,
                        max_ple_for_same_institution: degree.max_ple_for_same_institution ?? null,
                    }));
                    setDegrees(mappedDegrees);
                    setActiveLevel(mappedDegrees[0].degree);
                } else {
                    setDegrees([]);
                    setActiveLevel("");
                }
            } catch (error) {
                console.error('Error fetching degrees:', error);
                setDegrees([]);
                setActiveLevel("");
            } finally {
                setDegreesLoading(false);
            }
        };
        fetchDegrees();
    }, [selectedTerm]);

    // Fetch programs from Hub DB whenever term or degree tab changes
    useEffect(() => {
        if (!selectedTerm || !activeLevel) return;
        const controller = new AbortController();
        const fetchPrograms = async () => {
            setProgramsError(null);
            const cacheKey = `${selectedTerm}__${activeLevel}`;
            // Serve from cache instantly — no loading state needed
            if (programCacheRef.current[cacheKey]) {
                setPrograms(programCacheRef.current[cacheKey]);
                setCurrentPage(1);
                setProgramsLoading(false);
                return;
            }
            // First fetch: show loading overlay without clearing existing programs
            setProgramsLoading(true);
            const fetchStart = Date.now();
            try {
                const params = new URLSearchParams({ term_id: selectedTerm, degree_type: activeLevel });
                const response = await fetch(`/api/integrations/hub/programs-by-term/?${params}`, { signal: controller.signal });
                if (!response.ok) throw new Error(`Programs fetch failed: ${response.status}`);
                const data = await response.json();
                // Enforce a minimum 250ms transition so fast responses don't flash
                const elapsed = Date.now() - fetchStart;
                if (elapsed < 250) {
                    await new Promise<void>((resolve) => setTimeout(resolve, 250 - elapsed));
                }
                if (controller.signal.aborted) return;
                if (data.status === 'success' && Array.isArray(data.programs)) {
                    programCacheRef.current[cacheKey] = data.programs;
                    setPrograms(data.programs);
                    setCurrentPage(1);
                } else {
                    setPrograms([]);
                    setCurrentPage(1);
                }
            } catch (error) {
                if ((error as Error).name === 'AbortError') return;
                console.error('Error fetching programs:', error);
                setProgramsError('Failed to load programs');
            } finally {
                if (!controller.signal.aborted) setProgramsLoading(false);
            }
        };
        fetchPrograms();
        return () => controller.abort();
    }, [selectedTerm, activeLevel]);

    // Reset filters when term or degree changes
    useEffect(() => {
        setNameFilter("");
        setDurationFilter("");
        setLanguageFilter("");
        setFacultyFilter("");
        setThesisFilter("");
        setTuitionFeeMin("");
        setTuitionFeeMax("");
        setApplicationTypeFilter("");
    }, [selectedTerm, activeLevel]);

    // Reset page when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [nameFilter, durationFilter, languageFilter, facultyFilter, thesisFilter, tuitionFeeMin, tuitionFeeMax, applicationTypeFilter]);

    const currentTerm = terms.find((term) => term.id === selectedTerm);

    // Derive maxSelectable from the active degree's max_ple_for_same_institution
    const activeDegreeConfig = degrees.find((degree) => degree.degree === activeLevel);
    const maxSelectable = activeDegreeConfig?.max_ple_for_same_institution ?? 3;

    const heroImage = branding?.branding?.hero_image;
    const programsPerPage = branding?.branding?.programs_per_page ?? 10;
    const primaryColor = branding?.branding?.colors?.primary || '#1d3658';

    // Derived picklist options from loaded programs (unique, sorted, non-empty)
    const durationOptions = Array.from(new Set(programs.map((p) => p.duration).filter(Boolean))).sort() as string[];
    const languageOptions = Array.from(new Set(programs.map((p) => p.language).filter(Boolean))).sort() as string[];
    const facultyOptions  = Array.from(new Set(programs.map((p) => p.faculty).filter(Boolean))).sort() as string[];
    const thesisOptions   = Array.from(new Set(programs.map((p) => p.thesis_requirement).filter(Boolean))).sort() as string[];
    const applicationTypeOptions = Array.from(new Set(programs.flatMap((p) => p.available_application_types ?? []).filter(Boolean))).sort() as string[];

    // Tuition fee range helpers — slider bounds derived from loaded programs
    const programFees = programs.map((p) => p.tuition_fee).filter((fee): fee is number => fee !== null && fee !== undefined);
    const feeLowerBound = programFees.length > 0 ? Math.min(...programFees) : 0;
    const feeUpperBound = programFees.length > 0 ? Math.max(...programFees) : 0;
    const feeSliderMin = tuitionFeeMin !== "" ? parseFloat(tuitionFeeMin) : feeLowerBound;
    const feeSliderMax = tuitionFeeMax !== "" ? parseFloat(tuitionFeeMax) : feeUpperBound;
    const tuitionFeeRangeActive = programFees.length > 0 && (feeSliderMin > feeLowerBound || feeSliderMax < feeUpperBound);
    const currencyCode = programs.find((p) => p.currency_code)?.currency_code || "";

    // Apply all filters
    const filteredPrograms = programs.filter((prog) => {
        const nameMatch =
            nameFilter.length < 3 ||
            prog.name.toLowerCase().includes(nameFilter.toLowerCase());
        const durationMatch = !durationFilter || prog.duration === durationFilter;
        const languageMatch = !languageFilter || prog.language === languageFilter;
        const facultyMatch  = !facultyFilter  || prog.faculty === facultyFilter;
        const thesisMatch   = !thesisFilter   || prog.thesis_requirement === thesisFilter;
        const appTypeMatch = !applicationTypeFilter || (prog.available_application_types ?? []).length === 0 || (prog.available_application_types ?? []).includes(applicationTypeFilter);
        let feeMatch = true;
        if (tuitionFeeRangeActive) {
            if (prog.tuition_fee === null || prog.tuition_fee === undefined) {
                feeMatch = false;
            } else {
                feeMatch = prog.tuition_fee >= feeSliderMin && prog.tuition_fee <= feeSliderMax;
            }
        }
        return nameMatch && durationMatch && languageMatch && facultyMatch && thesisMatch && appTypeMatch && feeMatch;
    });

    const totalPages = Math.ceil(filteredPrograms.length / programsPerPage);
    const safePage = Math.min(currentPage, Math.max(totalPages, 1));
    const pagePrograms = filteredPrograms.slice((safePage - 1) * programsPerPage, safePage * programsPerPage);
    const activeFiltersCount =
        (nameFilter.length >= 3 ? 1 : 0) +
        (durationFilter ? 1 : 0) +
        (languageFilter ? 1 : 0) +
        (facultyFilter  ? 1 : 0) +
        (thesisFilter   ? 1 : 0) +
        (applicationTypeFilter ? 1 : 0) +
        (tuitionFeeRangeActive ? 1 : 0);

    const clearAllFilters = () => {
        setNameFilter(""); setDurationFilter(""); setLanguageFilter("");
        setFacultyFilter(""); setThesisFilter(""); setApplicationTypeFilter(""); setTuitionFeeMin(""); setTuitionFeeMax("");
    };
    const universityName = branding?.name || "University Portal";
    const programFinderBgColor = branding?.branding?.colors?.program_finder_background || '#16325c';

    return (
        <div className="min-h-screen flex flex-col bg-slate-50/50 font-sans">
            <Navbar />
            <main className="flex-grow pt-20">
                {/* Hero Banner Image */}
                <div className="relative h-[300px] md:h-[400px] overflow-hidden">
                    {loading ? (
                        <div className="w-full h-full bg-slate-200 animate-pulse" />
                    ) : heroImage ? (
                        <>
                            <img
                                src={heroImage}
                                alt={`${universityName} Campus`}
                                className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-primary/60 mix-blend-multiply" />
                        </>
                    ) : (
                        <div className="w-full h-full bg-gradient-to-br from-blue-600 via-blue-700 to-blue-900" />
                    )}
                    <div className="absolute inset-0 flex items-center justify-center text-center px-4">
                        <div className="max-w-3xl">
                            <h1 className="text-3xl md:text-5xl font-bold font-serif text-white mb-4 drop-shadow-md">
                                {branding?.branding?.landing_page?.title || "Application Portal"}
                            </h1>
                            <p className="text-white/90 text-lg md:text-xl max-w-2xl mx-auto drop-shadow-sm mb-8">
                                {branding?.branding?.landing_page?.subtitle || "Start your application or continue where you left off."}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="container px-4 md:px-6 mx-auto -mt-16 relative z-10 pb-16">
                    <div className="grid lg:grid-cols-3 gap-8 items-start">
                        <div className="lg:col-span-3 space-y-8">
                            {/* Quick Actions Card */}
                            <div className="bg-white p-4 sm:p-6 md:p-8 rounded-2xl shadow-xl border border-slate-200/60 flex flex-col sm:flex-row items-center justify-between gap-6">
                                <div className="space-y-2 text-center sm:text-left">
                                    <h2 className="text-xl font-bold text-slate-900">
                                        New to {universityName}?
                                    </h2>
                                    <p className="text-muted-foreground text-sm">
                                        Create an account and start your application.
                                    </p>
                                </div>
                                <div className="flex flex-col items-center sm:items-end gap-3 w-full sm:w-auto">
                                    <Button 
                                        onClick={() => navigate('/login')}
                                        className="w-full sm:w-auto text-white font-bold px-8 h-11 rounded-full shadow-lg border-none hover:opacity-90"
                                        style={{
                                            backgroundColor: branding?.branding?.colors?.primary || '#1d3658'
                                        }}
                                    >
                                        Start Application
                                    </Button>
                                    <button 
                                        onClick={() => navigate('/login')}
                                        className="text-sm font-bold text-slate-500 hover:text-primary transition-colors underline-offset-4 hover:underline"
                                    >
                                        Already applied? Login
                                    </button>
                                </div>
                            </div>

                            {/* Term & Program Selection */}
                            <Card
                                id="programs"
                                className="border-none shadow-xl overflow-hidden bg-white scroll-mt-24"
                            >
                                <CardHeader 
                                    className="text-white pb-6 sm:pb-8" 
                                    style={{ backgroundColor: programFinderBgColor }}
                                >
                                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
                                        <div className="space-y-1 text-center md:text-left">
                                            <CardTitle className="text-xl sm:text-2xl font-serif text-white">
                                                Program Finder
                                            </CardTitle>
                                            <CardDescription className="text-white/60">
                                                Find the right program for your goals
                                            </CardDescription>
                                        </div>
                                        <div className="w-full md:min-w-[240px] md:w-auto">
                                            <Label className="text-white/60 text-[10px] mb-2 block uppercase tracking-widest font-black text-center md:text-left">
                                                Select Intake Term
                                            </Label>
                                            <Select
                                                value={selectedTerm}
                                                onValueChange={(val) => {
                                                    setSelectedTerm(val);
                                                    setSelectedProgramIds([]);
                                                    setCurrentPage(1);
                                                }}
                                                disabled={termsLoading}
                                            >
                                                <SelectTrigger className="bg-white/10 border-white/20 text-white focus:ring-[#00A3FF] h-11 w-full overflow-hidden">
                                                    <SelectValue placeholder={termsLoading ? "Loading terms…" : "Select Term"} />
                                                </SelectTrigger>
                                                <SelectContent
                                                    position="popper"
                                                    sideOffset={4}
                                                    className="min-w-[var(--radix-select-trigger-width)]"
                                                    onCloseAutoFocus={(e) => e.preventDefault()}
                                                >
                                                    {terms.map((term) => (
                                                        <SelectItem key={term.id} value={term.id}>
                                                            {term.name}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    </div>
                                </CardHeader>

                                {/* Combined control bar: degree selector + filters */}
                                <div className="border-b border-slate-100">
                                    <div className="flex flex-col md:flex-row md:items-center">
                                        {/* Degree tabs — 2-col grid on mobile, single scrollable row on desktop */}
                                        <div className="flex-1 min-w-0 px-3 sm:px-6 pt-3 pb-2 md:overflow-x-auto md:[&::-webkit-scrollbar]:hidden md:[scrollbar-width:none] md:py-3">
                                            {degreesLoading ? (
                                                <div className="grid grid-cols-2 md:flex gap-2">
                                                    <div className="h-11 bg-slate-200 animate-pulse rounded-xl" />
                                                    <div className="h-11 bg-slate-200 animate-pulse rounded-xl" />
                                                    <div className="h-11 bg-slate-200 animate-pulse rounded-xl" />
                                                    <div className="h-11 bg-slate-200 animate-pulse rounded-xl" />
                                                </div>
                                            ) : (
                                                <div className="grid grid-cols-2 md:flex md:flex-nowrap gap-2 md:w-auto md:justify-center">
                                                    {degrees.map((degree) => {
                                                        const isActive = activeLevel === degree.degree;
                                                        const isPhd = degree.degree === "PhD" || degree.degree === "Doctorate";
                                                        return (
                                                            <button
                                                                key={degree.id}
                                                                type="button"
                                                                onClick={() => {
                                                                    setActiveLevel(degree.degree);
                                                                    setSelectedProgramIds([]);
                                                                    setCurrentPage(1);
                                                                }}
                                                                className={`flex items-center justify-center gap-2 h-11 px-4 rounded-xl border font-semibold text-sm transition-all duration-200 whitespace-nowrap md:shrink-0
                                                                    ${isActive
                                                                        ? "text-white border-transparent shadow-md"
                                                                        : "bg-white border-slate-200 text-slate-700 hover:border-slate-400 hover:shadow-sm"
                                                                    }`}
                                                                style={isActive ? { backgroundColor: programFinderBgColor } : {}}
                                                            >
                                                                {isPhd
                                                                    ? <Award size={16} strokeWidth={isActive ? 2.5 : 1.8} />
                                                                    : <GraduationCap size={16} strokeWidth={isActive ? 2.5 : 1.8} />
                                                                }
                                                                {degree.degree}
                                                                {isActive && programsLoading && (
                                                                    <span className="inline-block h-3.5 w-3.5 rounded-full border-2 border-white/40 border-t-white animate-spin ml-0.5" />
                                                                )}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>

                                        {/* Divider — vertical on desktop, horizontal on mobile */}
                                        <div className="hidden md:block w-px h-8 bg-slate-200 shrink-0" />
                                        <div className="md:hidden h-px bg-slate-100 mx-3" />

                                        {/* Filters toggle — full width on mobile, compact on desktop */}
                                        <button
                                            type="button"
                                            onClick={() => setFiltersOpen((prev) => !prev)}
                                            aria-expanded={filtersOpen}
                                            aria-controls="program-filters-panel"
                                            className="flex items-center justify-center gap-2 shrink-0 h-11 px-3 md:px-4 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors w-full md:w-auto"
                                        >
                                            <SlidersHorizontal size={15} />
                                            Filters
                                            {activeFiltersCount > 0 && (
                                                <span
                                                    className="inline-flex items-center justify-center h-5 min-w-5 rounded-full text-white text-[11px] font-bold px-1.5"
                                                    style={{ backgroundColor: primaryColor }}
                                                >
                                                    {activeFiltersCount}
                                                </span>
                                            )}
                                            {filtersOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                        </button>
                                    </div>

                                    {filtersOpen && (
                                        <div id="program-filters-panel" className="px-3 sm:px-6 pb-4 pt-1 grid grid-cols-1 sm:grid-cols-2 gap-4 bg-slate-50/60 animate-in fade-in slide-in-from-top-1 duration-150">

                                            {/* Program Name */}
                                            <div className="space-y-1.5">
                                                <Label className="text-xs text-slate-500 uppercase tracking-widest font-black">
                                                    Program Name
                                                </Label>
                                                <div className="relative">
                                                    <input
                                                        type="text"
                                                        value={nameFilter}
                                                        onChange={(e) => setNameFilter(e.target.value)}
                                                        placeholder="Type at least 3 characters…"
                                                        className="w-full h-10 rounded-lg border border-slate-200 bg-white px-3 pr-8 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-offset-0"
                                                        style={{ '--tw-ring-color': primaryColor } as React.CSSProperties}
                                                    />
                                                    {nameFilter && (
                                                        <button
                                                            type="button"
                                                            onClick={() => setNameFilter("")}
                                                            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                                            aria-label="Clear name filter"
                                                        >
                                                            <X size={14} />
                                                        </button>
                                                    )}
                                                </div>
                                                {nameFilter.length > 0 && nameFilter.length < 3 && (
                                                    <p className="text-[11px] text-slate-400">
                                                        {3 - nameFilter.length} more character{3 - nameFilter.length !== 1 ? "s" : ""} to filter
                                                    </p>
                                                )}
                                            </div>

                                            {/* Duration */}
                                            <div className="space-y-1.5">
                                                <Label className="text-xs text-slate-500 uppercase tracking-widest font-black">
                                                    Duration
                                                </Label>
                                                <Select
                                                    value={durationFilter || "__all__"}
                                                    onValueChange={(val) => setDurationFilter(val === "__all__" ? "" : val)}
                                                    disabled={durationOptions.length === 0}
                                                >
                                                    <SelectTrigger className="h-10 w-full bg-white border-slate-200 text-sm">
                                                        <SelectValue placeholder="All durations" />
                                                    </SelectTrigger>
                                                    <SelectContent position="popper" sideOffset={4} className="min-w-[var(--radix-select-trigger-width)]" onCloseAutoFocus={(e) => e.preventDefault()}>
                                                        <SelectItem value="__all__">All durations</SelectItem>
                                                        {durationOptions.map((dur) => (
                                                            <SelectItem key={dur} value={dur}>{formatDuration(dur)}</SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </div>

                                            {/* Language of Instruction */}
                                            <div className="space-y-1.5">
                                                <Label className="text-xs text-slate-500 uppercase tracking-widest font-black">
                                                    Language of Instruction
                                                </Label>
                                                <Select
                                                    value={languageFilter || "__all__"}
                                                    onValueChange={(val) => setLanguageFilter(val === "__all__" ? "" : val)}
                                                    disabled={languageOptions.length === 0}
                                                >
                                                    <SelectTrigger className="h-10 w-full bg-white border-slate-200 text-sm">
                                                        <SelectValue placeholder="All languages" />
                                                    </SelectTrigger>
                                                    <SelectContent position="popper" sideOffset={4} className="min-w-[var(--radix-select-trigger-width)]" onCloseAutoFocus={(e) => e.preventDefault()}>
                                                        <SelectItem value="__all__">All languages</SelectItem>
                                                        {languageOptions.map((lang) => (
                                                            <SelectItem key={lang} value={lang}>{lang}</SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </div>

                                            {/* Faculty */}
                                            <div className="space-y-1.5">
                                                <Label className="text-xs text-slate-500 uppercase tracking-widest font-black">
                                                    Faculty
                                                </Label>
                                                <Select
                                                    value={facultyFilter || "__all__"}
                                                    onValueChange={(val) => setFacultyFilter(val === "__all__" ? "" : val)}
                                                    disabled={facultyOptions.length === 0}
                                                >
                                                    <SelectTrigger className="h-10 w-full bg-white border-slate-200 text-sm">
                                                        <SelectValue placeholder="All faculties" />
                                                    </SelectTrigger>
                                                    <SelectContent position="popper" sideOffset={4} className="min-w-[var(--radix-select-trigger-width)]" onCloseAutoFocus={(e) => e.preventDefault()}>
                                                        <SelectItem value="__all__">All faculties</SelectItem>
                                                        {facultyOptions.map((fac) => (
                                                            <SelectItem key={fac} value={fac}>{fac}</SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </div>

                                            {/* Thesis Requirement */}
                                            <div className="space-y-1.5">
                                                <Label className="text-xs text-slate-500 uppercase tracking-widest font-black">
                                                    Thesis
                                                </Label>
                                                <Select
                                                    value={thesisFilter || "__all__"}
                                                    onValueChange={(val) => setThesisFilter(val === "__all__" ? "" : val)}
                                                    disabled={thesisOptions.length === 0}
                                                >
                                                    <SelectTrigger className="h-10 w-full bg-white border-slate-200 text-sm">
                                                        <SelectValue placeholder="All" />
                                                    </SelectTrigger>
                                                    <SelectContent position="popper" sideOffset={4} className="min-w-[var(--radix-select-trigger-width)]" onCloseAutoFocus={(e) => e.preventDefault()}>
                                                        <SelectItem value="__all__">All</SelectItem>
                                                        {thesisOptions.map((opt) => (
                                                            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </div>

                                            {/* Application Type */}
                                            <div className="space-y-1.5">
                                                <Label className="text-xs text-slate-500 uppercase tracking-widest font-black">
                                                    Application Type
                                                </Label>
                                                <Select
                                                    value={applicationTypeFilter || "__all__"}
                                                    onValueChange={(val) => setApplicationTypeFilter(val === "__all__" ? "" : val)}
                                                    disabled={applicationTypeOptions.length === 0}
                                                >
                                                    <SelectTrigger className="h-10 w-full bg-white border-slate-200 text-sm">
                                                        <SelectValue placeholder="All types" />
                                                    </SelectTrigger>
                                                    <SelectContent position="popper" sideOffset={4} className="min-w-[var(--radix-select-trigger-width)]" onCloseAutoFocus={(e) => e.preventDefault()}>
                                                        <SelectItem value="__all__">All types</SelectItem>
                                                        {applicationTypeOptions.map((opt) => (
                                                            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </div>

                                            {/* Tuition Fee Range */}
                                            <div className="space-y-1.5">
                                                <Label className="text-xs text-slate-500 uppercase tracking-widest font-black">
                                                    Tuition Fee{currencyCode ? ` (${currencyCode})` : ""}
                                                </Label>
                                                {/* Dual-range slider */}
                                                {programFees.length > 0 && feeLowerBound < feeUpperBound ? (
                                                    <>
                                                        {/* Thumb-only pointer-events — scoped to this slider */}
                                                        <style>{`
                                                            .fee-range-input { pointer-events: none; }
                                                            .fee-range-input::-webkit-slider-thumb { pointer-events: all; cursor: pointer; }
                                                            .fee-range-input::-moz-range-thumb { pointer-events: all; cursor: pointer; }
                                                        `}</style>
                                                        <div className="flex justify-between text-xs text-slate-500 mb-2">
                                                            <span>{feeSliderMin.toLocaleString()}{currencyCode ? ` ${currencyCode}` : ""}</span>
                                                            <span>{feeSliderMax.toLocaleString()}{currencyCode ? ` ${currencyCode}` : ""}</span>
                                                        </div>
                                                        <div className="relative h-6 flex items-center">
                                                            {/* background track */}
                                                            <div className="absolute inset-x-0 h-1.5 rounded-full bg-slate-200" />
                                                            {/* active fill track */}
                                                            <div
                                                                className="absolute h-1.5 rounded-full pointer-events-none"
                                                                style={{
                                                                    backgroundColor: primaryColor,
                                                                    left: `${((feeSliderMin - feeLowerBound) / (feeUpperBound - feeLowerBound)) * 100}%`,
                                                                    right: `${100 - ((feeSliderMax - feeLowerBound) / (feeUpperBound - feeLowerBound)) * 100}%`,
                                                                }}
                                                            />
                                                            {/* min thumb */}
                                                            <input
                                                                type="range"
                                                                min={feeLowerBound}
                                                                max={feeUpperBound}
                                                                value={feeSliderMin}
                                                                onChange={(e) => {
                                                                    const next = Math.min(Number(e.target.value), feeSliderMax);
                                                                    setTuitionFeeMin(next === feeLowerBound ? "" : String(next));
                                                                }}
                                                                className="fee-range-input absolute w-full h-full appearance-none bg-transparent [&::-webkit-slider-runnable-track]:bg-transparent [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:border-2"
                                                                style={{
                                                                    zIndex: feeSliderMin >= feeSliderMax ? 5 : 4,
                                                                    margin: 0,
                                                                    padding: 0,
                                                                }}
                                                            />
                                                            {/* max thumb */}
                                                            <input
                                                                type="range"
                                                                min={feeLowerBound}
                                                                max={feeUpperBound}
                                                                value={feeSliderMax}
                                                                onChange={(e) => {
                                                                    const next = Math.max(Number(e.target.value), feeSliderMin);
                                                                    setTuitionFeeMax(next === feeUpperBound ? "" : String(next));
                                                                }}
                                                                className="fee-range-input absolute w-full h-full appearance-none bg-transparent [&::-webkit-slider-runnable-track]:bg-transparent [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:border-2"
                                                                style={{
                                                                    zIndex: feeSliderMin >= feeSliderMax ? 4 : 5,
                                                                    margin: 0,
                                                                    padding: 0,
                                                                }}
                                                            />
                                                        </div>
                                                    </>
                                                ) : (
                                                    <p className="text-xs text-slate-400 italic">No fee data available for this selection.</p>
                                                )}
                                            </div>

                                            {/* Active filter tags + clear all */}
                                            {activeFiltersCount > 0 && (
                                                <div className="sm:col-span-2 flex items-center gap-2 flex-wrap pt-1">
                                                    {nameFilter.length >= 3 && (
                                                        <span className="inline-flex items-center gap-1 bg-white border border-slate-200 rounded-full px-2.5 py-1 text-xs text-slate-700 font-medium">
                                                            Name: "{nameFilter}"
                                                            <button type="button" onClick={() => setNameFilter("")} className="text-slate-400 hover:text-slate-700 ml-0.5"><X size={11} /></button>
                                                        </span>
                                                    )}
                                                    {durationFilter && (
                                                        <span className="inline-flex items-center gap-1 bg-white border border-slate-200 rounded-full px-2.5 py-1 text-xs text-slate-700 font-medium">
                                                            Duration: {formatDuration(durationFilter)}
                                                            <button type="button" onClick={() => setDurationFilter("")} className="text-slate-400 hover:text-slate-700 ml-0.5"><X size={11} /></button>
                                                        </span>
                                                    )}
                                                    {languageFilter && (
                                                        <span className="inline-flex items-center gap-1 bg-white border border-slate-200 rounded-full px-2.5 py-1 text-xs text-slate-700 font-medium">
                                                            Language: {languageFilter}
                                                            <button type="button" onClick={() => setLanguageFilter("")} className="text-slate-400 hover:text-slate-700 ml-0.5"><X size={11} /></button>
                                                        </span>
                                                    )}
                                                    {facultyFilter && (
                                                        <span className="inline-flex items-center gap-1 bg-white border border-slate-200 rounded-full px-2.5 py-1 text-xs text-slate-700 font-medium">
                                                            Faculty: {facultyFilter}
                                                            <button type="button" onClick={() => setFacultyFilter("")} className="text-slate-400 hover:text-slate-700 ml-0.5"><X size={11} /></button>
                                                        </span>
                                                    )}
                                                    {thesisFilter && (
                                                        <span className="inline-flex items-center gap-1 bg-white border border-slate-200 rounded-full px-2.5 py-1 text-xs text-slate-700 font-medium">
                                                            Thesis: {thesisFilter}
                                                            <button type="button" onClick={() => setThesisFilter("")} className="text-slate-400 hover:text-slate-700 ml-0.5"><X size={11} /></button>
                                                        </span>
                                                    )}
                                                    {applicationTypeFilter && (
                                                        <span className="inline-flex items-center gap-1 bg-white border border-slate-200 rounded-full px-2.5 py-1 text-xs text-slate-700 font-medium">
                                                            Type: {applicationTypeFilter}
                                                            <button type="button" onClick={() => setApplicationTypeFilter("")} className="text-slate-400 hover:text-slate-700 ml-0.5"><X size={11} /></button>
                                                        </span>
                                                    )}
                                                    {tuitionFeeRangeActive && (
                                                        <span className="inline-flex items-center gap-1 bg-white border border-slate-200 rounded-full px-2.5 py-1 text-xs text-slate-700 font-medium">
                                                            Fee: {feeSliderMin.toLocaleString()} – {feeSliderMax.toLocaleString()}{currencyCode ? ` ${currencyCode}` : ""}
                                                            <button type="button" onClick={() => { setTuitionFeeMin(""); setTuitionFeeMax(""); }} className="text-slate-400 hover:text-slate-700 ml-0.5"><X size={11} /></button>
                                                        </span>
                                                    )}
                                                    <button
                                                        type="button"
                                                        onClick={clearAllFilters}
                                                        className="text-xs text-slate-500 hover:text-slate-800 underline underline-offset-2 font-medium"
                                                    >
                                                        Clear all
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>

                                <CardContent className="p-0">
                                    <Tabs
                                        value={activeLevel}
                                        onValueChange={(val) => {
                                            setActiveLevel(val);
                                            setSelectedProgramIds([]);
                                            setCurrentPage(1);
                                        }}
                                        className="w-full"
                                    >
                                        <TabsContent value={activeLevel} className="m-0">
                                            <div className="relative">
                                                {/* Translucent overlay — keeps existing content visible while new data loads */}
                                                {programsLoading && (
                                                    <div className="absolute inset-0 z-10 bg-white/30 pointer-events-none" aria-hidden="true" />
                                                )}
                                                <div className={`divide-y divide-slate-50 transition-opacity duration-300 ${programsLoading ? 'opacity-90' : 'opacity-100'}`}>
                                                {programsError ? (
                                                    <div className="p-12 text-center">
                                                        <p className="text-red-500 mb-2">Failed to load programs</p>
                                                        <p className="text-muted-foreground text-sm">{programsError}</p>
                                                    </div>
                                                ) : programs.length > 0 ? (
                                                    <>
                                                        {/* Selection info banner */}
                                                        {maxSelectable > 1 && (
                                                            <div className="px-4 sm:px-6 py-3 border-b border-slate-100">
                                                                <div className="flex items-start gap-3 bg-blue-50 border border-blue-100 rounded-lg px-4 py-3">
                                                                    <Info size={16} className="text-blue-400 shrink-0 mt-0.5" />
                                                                    <div className="min-w-0">
                                                                        <p className="text-sm text-blue-900 font-medium">
                                                                            Select up to <span className="font-bold">{maxSelectable}</span> programs in order of preference.
                                                                            {selectedProgramIds.length > 0 && (
                                                                                <span className="ml-1.5 text-emerald-700 font-semibold">{selectedProgramIds.length} selected.</span>
                                                                            )}
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                        {filteredPrograms.length === 0 ? (
                                                            <div className="p-12 text-center">
                                                                <p className="text-muted-foreground mb-2">No programs match your filters.</p>
                                                                <button
                                                                    type="button"
                                                                    onClick={clearAllFilters}
                                                                    className="text-sm font-medium underline underline-offset-2 text-slate-500 hover:text-slate-800"
                                                                >
                                                                    Clear filters
                                                                </button>
                                                            </div>
                                                        ) : null}
                                                        {pagePrograms.map((program) => {
                                                            const selectionIndex = selectedProgramIds.indexOf(program.id);
                                                            const isSelected = selectionIndex !== -1;
                                                            const isAtCap = !isSelected && selectedProgramIds.length >= maxSelectable;

                                                            const handleToggle = () => {
                                                                if (isSelected) {
                                                                    setSelectedProgramIds((prev) => prev.filter((programId) => programId !== program.id));
                                                                } else if (!isAtCap) {
                                                                    setSelectedProgramIds((prev) =>
                                                                        prev.length >= maxSelectable ? prev : [...prev, program.id]
                                                                    );
                                                                }
                                                            };

                                                            return (
                                                                <div
                                                                    key={program.id}
                                                                    className={`p-4 sm:p-6 transition-all flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 group
                                                                        ${isSelected
                                                                            ? "bg-[#001B44]/[0.02] border-l-4 border-l-[#001B44]"
                                                                            : isAtCap
                                                                                ? "opacity-40"
                                                                                : "hover:bg-slate-50/80"
                                                                        }`}
                                                                    style={{ '--btn-hover-bg': branding?.branding?.colors?.secondary || '#16325c' } as React.CSSProperties}
                                                                >
                                                                    <div className="space-y-2 text-left min-w-0">
                                                                        <h3 className={`font-bold text-base sm:text-lg leading-snug transition-colors ${isSelected ? "text-[#001B44]" : "text-slate-900 group-hover:text-primary"}`}>
                                                                            {program.name}
                                                                        </h3>
                                                                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs sm:text-sm text-muted-foreground">
                                                                            <span className="flex items-center gap-1">
                                                                                {program.degree_type === "PhD" || program.degree_type === "Doctorate"
                                                                                    ? <Award size={13} style={{ color: branding?.branding?.colors?.accent || '#517dee' }} />
                                                                                    : <GraduationCap size={13} style={{ color: branding?.branding?.colors?.accent || '#517dee' }} />
                                                                                }
                                                                                {program.degree_type}
                                                                            </span>
                                                                            {program.faculty && (
                                                                                <span className="flex items-center gap-1 min-w-0">
                                                                                    <MapPin size={13} className="shrink-0" style={{ color: branding?.branding?.colors?.accent || '#517dee' }} />
                                                                                    <span className="truncate max-w-[160px] sm:max-w-none">{program.faculty}</span>
                                                                                </span>
                                                                            )}
                                                                            {program.language && (
                                                                                <span className="flex items-center gap-1">
                                                                                    <Languages size={13} style={{ color: branding?.branding?.colors?.accent || '#517dee' }} />
                                                                                    {program.language}
                                                                                </span>
                                                                            )}
                                                                            {program.duration && (
                                                                                <span className="flex items-center gap-1">
                                                                                    <Clock size={13} style={{ color: branding?.branding?.colors?.accent || '#517dee' }} />
                                                                                    {formatDuration(program.duration)}
                                                                                </span>
                                                                            )}
                                                                            {program.thesis_requirement && (
                                                                                <span className="flex items-center gap-1">
                                                                                    <BookOpen size={13} style={{ color: branding?.branding?.colors?.accent || '#517dee' }} />
                                                                                    {program.thesis_requirement}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                        {program.tuition_fee != null && (
                                                                            <div className="flex items-center gap-1.5 mt-0.5">
                                                                                <Tag size={12} className="text-slate-400 shrink-0" />
                                                                                <span className="text-sm font-bold text-slate-700">
                                                                                    {program.tuition_fee.toLocaleString("en-US", { style: "currency", currency: program.currency_code || "USD", maximumFractionDigits: 0 })}
                                                                                </span>
                                                                                <span className="text-xs text-slate-400 font-medium">/ year</span>
                                                                            </div>
                                                                        )}
                                                                        {(program.available_application_types ?? []).length > 0 && (
                                                                            <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
                                                                                {(program.available_application_types ?? []).map((appType) => (
                                                                                    <span
                                                                                        key={appType}
                                                                                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200"
                                                                                    >
                                                                                        <UserCheck size={11} />
                                                                                        {appType}
                                                                                    </span>
                                                                                ))}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                    <Button
                                                                        onClick={handleToggle}
                                                                        disabled={isAtCap}
                                                                        variant={isSelected ? "default" : "outline"}
                                                                        title={isAtCap ? `Maximum of ${maxSelectable} programs reached` : undefined}
                                                                        className={`shrink-0 self-end sm:self-auto w-full sm:w-auto font-bold transition-all h-10 sm:h-11 text-sm ${
                                                                            isSelected
                                                                                ? "bg-emerald-500 hover:bg-emerald-600 text-white border-none"
                                                                                : "border-slate-200 group-hover:text-white group-hover:bg-[--btn-hover-bg] group-hover:border-transparent"
                                                                        } ${isAtCap ? "cursor-not-allowed" : ""}`}
                                                                    >
                                                                        {isSelected ? (
                                                                            <>
                                                                                {maxSelectable > 1 && (
                                                                                    <span className="mr-1.5 font-black">#{selectionIndex + 1}</span>
                                                                                )}
                                                                                Selected ✓
                                                                            </>
                                                                        ) : (
                                                                            <>
                                                                                Select Program
                                                                                <ArrowRight className="ml-1.5 h-4 w-4" />
                                                                            </>
                                                                        )}
                                                                    </Button>
                                                                </div>
                                                            );
                                                        })}
                                                        {totalPages > 1 && (
                                                            <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-t border-slate-100">
                                                                <span className="text-sm text-muted-foreground">
                                                                    {(safePage - 1) * programsPerPage + 1}–{Math.min(safePage * programsPerPage, filteredPrograms.length)} of {filteredPrograms.length}
                                                                </span>
                                                                <div className="flex items-center gap-2">
                                                                    <Button
                                                                        variant="outline"
                                                                        size="sm"
                                                                        onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                                                                        disabled={safePage === 1}
                                                                        className="h-8 rounded-full border-slate-200"
                                                                    >
                                                                        <ChevronLeft className="h-4 w-4" />
                                                                    </Button>
                                                                    <Button
                                                                        variant="outline"
                                                                        size="sm"
                                                                        onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                                                                        disabled={safePage === totalPages}
                                                                        className="h-8 rounded-full border-slate-200"
                                                                    >
                                                                        <ChevronRight className="h-4 w-4" />
                                                                    </Button>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </>
                                                ) : (
                                                    <div className="p-12 text-center">
                                                        <p className="text-muted-foreground">
                                                            No programs found for {activeLevel} in{" "}
                                                            {currentTerm?.name ?? "this term"}.
                                                        </p>
                                                    </div>
                                                )}
                                                </div>{/* end divide-y */}
                                            </div>{/* end relative */}
                                        </TabsContent>
                                    </Tabs>
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                </div>
            </main>

            {/* Floating Basket Widget */}
            <ProgramBasketWidget
                selectedProgramIds={selectedProgramIds}
                programs={programs}
                maxSelectable={maxSelectable}
                onRemove={(programId) => setSelectedProgramIds(selectedProgramIds.filter((id) => id !== programId))}
                onReorder={setSelectedProgramIds}
                onApplyNow={() => {
                    const programSelection = {
                        term: selectedTerm,
                        degree: activeLevel,
                        programs: selectedProgramIds.map(String),
                        ...(applicationTypeFilter ? { applicationType: applicationTypeFilter } : {}),
                        timestamp: Date.now(),
                    };
                    localStorage.setItem('pendingProgramSelection', JSON.stringify(programSelection));
                    const params = new URLSearchParams({
                        term: selectedTerm,
                        degree: activeLevel,
                        programs: selectedProgramIds.join(','),
                    });
                    navigate(`/register?${params}`);
                }}
            />

            <Footer />
        </div>
    );
}
